import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { authAPI, orderAPI, paymentAPI } from '../services/api';

const COUNTRY_CODES = ['+91', '+1', '+44', '+971', '+61'];

const getPhoneParts = (value) => {
  const normalized = String(value || '').replace(/\s/g, '');
  const countryCode = COUNTRY_CODES.find((code) => normalized.startsWith(code)) || '+91';
  return { countryCode, number: normalized.startsWith(countryCode) ? normalized.slice(countryCode.length) : normalized };
};

const formatPhone = (value, countryCode = '+91') => {
  const digits = String(value || '').replace(/\D/g, '');
  if (countryCode === '+1') {
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }
  return digits.length > 5 ? `${digits.slice(0, 5)} ${digits.slice(5, 10)}` : digits;
};

export default function Account() {
  const { user, updateUser } = useAuth();
  const { clearCart } = useCart();
  const [profile, setProfile] = useState(user);
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState('');
  const [contactForm, setContactForm] = useState({ phone: '', phoneCountry: '+91', whatsappPhone: '', whatsappCountry: '+91' });
  const [editingContact, setEditingContact] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [retryingOrderId, setRetryingOrderId] = useState('');

  useEffect(() => {
    if (!user) return;
    Promise.all([authAPI.getProfile(), orderAPI.getUserOrders()])
      .then(([profileResponse, ordersResponse]) => {
        setProfile(profileResponse.data.user);
        const phone = getPhoneParts(profileResponse.data.user.phone);
        const whatsappPhone = getPhoneParts(profileResponse.data.user.whatsappPhone);
        setContactForm({
          phone: formatPhone(phone.number, phone.countryCode),
          phoneCountry: phone.countryCode,
          whatsappPhone: formatPhone(whatsappPhone.number, whatsappPhone.countryCode),
          whatsappCountry: whatsappPhone.countryCode
        });
        setOrders(ordersResponse.data.orders || []);
      })
      .catch(() => setError('Unable to load your account information.'));
  }, [user]);

  if (!user) return <Navigate to="/login" replace />;

  const formatDate = (value) => value
    ? new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : 'Not specified';

  const formatImage = (item) => item.productId?.images?.find((image) => image.isPrimary)?.url
    || item.productId?.images?.[0]?.url
    || '';

  const handleContactChange = (event) => {
    const { name, value } = event.target;
    const countryCode = name === 'phone' ? contactForm.phoneCountry : contactForm.whatsappCountry;
    setContactForm((current) => ({ ...current, [name]: formatPhone(value, countryCode) }));
  };

  const handleContactCountryChange = (event) => {
    const { name, value } = event.target;
    const numberField = name === 'phoneCountry' ? 'phone' : 'whatsappPhone';
    setContactForm((current) => ({
      ...current,
      [name]: value,
      [numberField]: formatPhone(current[numberField], value)
    }));
  };

  const saveContactNumbers = async (event) => {
    event.preventDefault();
    setSavingContact(true);
    setContactMessage('');
    try {
      const response = await authAPI.updateProfile({
        name: profile.name,
        phone: `${contactForm.phoneCountry}${contactForm.phone.replace(/\D/g, '')}`,
        whatsappPhone: `${contactForm.whatsappCountry}${contactForm.whatsappPhone.replace(/\D/g, '')}`
      });
      setProfile(response.data.user);
      updateUser(response.data.user);
      setEditingContact(false);
      setContactMessage('Contact numbers updated successfully.');
    } catch (requestError) {
      setContactMessage(requestError.response?.data?.message || 'Unable to update contact numbers.');
    } finally {
      setSavingContact(false);
    }
  };

  const continuePayment = async (order) => {
    setRetryingOrderId(order._id);
    setError('');
    try {
      const razorpayResponse = await paymentAPI.createRazorpayOrder({ orderId: order._id });
      const razorpayOrder = razorpayResponse.data.order;
      const razorpayKey = razorpayOrder?.key_id;
      if (!window.Razorpay || !razorpayKey) {
        throw new Error('Razorpay checkout is not available right now.');
      }

      const razorpay = new window.Razorpay({
        key: razorpayKey,
        amount: razorpayOrder.amount,
        currency: razorpayOrder.currency || 'INR',
        name: 'Starlight Threading',
        description: `Payment for ${order.orderId || 'your order'}`,
        order_id: razorpayOrder.id,
        handler: async (successResponse) => {
          try {
            await paymentAPI.verifyPayment({
              razorpay_order_id: successResponse.razorpay_order_id,
              razorpay_payment_id: successResponse.razorpay_payment_id,
              razorpay_signature: successResponse.razorpay_signature,
              orderId: order._id
            });
            await clearCart();
            const ordersResponse = await orderAPI.getUserOrders();
            setOrders(ordersResponse.data.orders || []);
          } catch (verifyError) {
            setError(verifyError.response?.data?.message || 'Payment verification failed. Please try again.');
          } finally {
            setRetryingOrderId('');
          }
        },
        prefill: {
          name: profile?.name || user.name,
          email: profile?.email || user.email,
          contact: profile?.whatsappPhone || profile?.phone || user.phone
        },
        theme: { color: '#c94f7c' },
        modal: { ondismiss: () => setRetryingOrderId('') }
      });

      razorpay.on('payment.failed', (failureResponse) => {
        setError(failureResponse.error?.description || 'Payment failed. You can try again.');
        setRetryingOrderId('');
      });
      razorpay.open();
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message || 'Unable to continue payment.');
      setRetryingOrderId('');
    }
  };

  return (
    <div className="account-page">
      <div className="account-header">
        <p className="eyebrow">Your account</p>
        <h1>Welcome, {profile?.name || profile?.email}</h1>
        <p>{profile?.email}</p>
      </div>
      {error && <p className="auth-error">{error}</p>}
      <section className="account-section">
        <h2>Account information</h2>
        {!editingContact ? (
          <div className="account-contact-summary">
            <div><span>Phone number</span><strong>{profile?.phone || 'Not provided'}</strong></div>
            <div><span>WhatsApp number</span><strong>{profile?.whatsappPhone || 'Not provided'}</strong></div>
            <button type="button" className="secondary-btn" onClick={() => { setContactMessage(''); setEditingContact(true); }}>Edit</button>
          </div>
        ) : <form className="account-contact-form" onSubmit={saveContactNumbers}>
          <label>Phone number<div className="phone-input-group"><select name="phoneCountry" value={contactForm.phoneCountry} onChange={handleContactCountryChange} aria-label="Phone country code"><option value="+91">India (+91)</option><option value="+1">United States (+1)</option><option value="+44">United Kingdom (+44)</option><option value="+971">UAE (+971)</option><option value="+61">Australia (+61)</option></select><input name="phone" type="tel" value={contactForm.phone} onChange={handleContactChange} placeholder="98765 43210" pattern="[0-9 ()-]{7,20}" required /></div></label>
          <label>WhatsApp number<div className="phone-input-group"><select name="whatsappCountry" value={contactForm.whatsappCountry} onChange={handleContactCountryChange} aria-label="WhatsApp country code"><option value="+91">India (+91)</option><option value="+1">United States (+1)</option><option value="+44">United Kingdom (+44)</option><option value="+971">UAE (+971)</option><option value="+61">Australia (+61)</option></select><input name="whatsappPhone" type="tel" value={contactForm.whatsappPhone} onChange={handleContactChange} placeholder="98765 43210" pattern="[0-9 ()-]{7,20}" required /></div></label>
          <div className="account-contact-actions">
            <button type="submit" className="primary-btn" disabled={savingContact}>{savingContact ? 'Saving...' : 'Update contact numbers'}</button>
            <button type="button" className="secondary-btn" onClick={() => { setEditingContact(false); setContactMessage(''); }}>Cancel</button>
          </div>
          {contactMessage && <p className="contact-update-message">{contactMessage}</p>}
        </form>}
      </section>
      <section className="account-section">
        <h2>Order history</h2>
        {orders.length === 0 ? (
          <p>You have not placed an order yet. <Link to="/shop">Start shopping</Link></p>
        ) : <div className="account-order-list">{orders.map((order) => (
          <article className="account-order" key={order._id}>
            <div className="account-order-header">
              <div>
                <span className="account-order-label">Order</span>
                <strong>{order.orderId || order._id}</strong>
                <small>Placed {formatDate(order.createdAt)}</small>
              </div>
              <span className={`order-status order-status-${order.orderStatus}`}>{order.orderStatus}</span>
            </div>
            <div className="account-order-items">
              {order.items?.map((item, index) => {
                const product = item.productId || {};
                const productName = product.name || item.name || 'Product';
                return (
                  <div className="account-order-item" key={`${item.productId?._id || item.productId || productName}-${index}`}>
                    {formatImage(item) ? <img src={formatImage(item)} alt={productName} /> : <div className="account-order-image-placeholder">ST</div>}
                    <div className="account-order-item-info">
                      <strong>{productName}</strong>
                      <span>Qty {item.quantity || 1}{item.size ? ` · Size ${item.size}` : ''}{item.color ? ` · ${item.color}` : ''}</span>
                    </div>
                    <b>₹{(Number(item.price || 0) * Number(item.quantity || 1)).toLocaleString('en-IN')}</b>
                  </div>
                );
              })}
            </div>
            <div className="account-order-footer">
              <span>{order.deliveryDate ? `Dispatch date: ${formatDate(order.deliveryDate)}` : 'Dispatch date to be confirmed'}</span>
              <strong>Total ₹{Number(order.finalAmount || order.totalAmount || 0).toLocaleString('en-IN')}</strong>
            </div>
            {order.paymentStatus !== 'completed' && order.orderStatus === 'pending' && (
              <button
                type="button"
                className="primary-btn"
                disabled={retryingOrderId === order._id}
                onClick={() => continuePayment(order)}
              >
                {retryingOrderId === order._id ? 'Preparing payment...' : 'Continue payment'}
              </button>
            )}
          </article>
        ))}</div>}
      </section>
    </div>
  );
}